import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { orgService } from '../services/orgService'
import { formatDuration, formatTimePT } from '../utils/formatTime'
import { formatPace } from '../utils/paceCalc'
import { MapView } from '../components/map/MapView'
import type { LocationPoint } from '../types/index'

export function SpectatorPage() {
  const { slug, bib } = useParams<{ slug: string; bib: string }>()

  const [data, setData] = useState<Awaited<ReturnType<typeof orgService.getTeamWatchData>> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [tab, setTab] = useState<'runners' | 'log'>('runners')
  const [fullCourseTrigger, setFullCourseTrigger] = useState(0)

  const load = async (isInitial = false) => {
    if (!slug || !bib) return
    try {
      const result = await orgService.getTeamWatchData(slug, Number(bib))
      setData(result)
      setLastUpdated(new Date())
      if (isInitial) setTimeout(() => setFullCourseTrigger(t => t + 1), 150)
    } catch {
      if (isInitial) setError('Team not found. Check the URL and try again.')
    } finally {
      if (isInitial) setLoading(false)
    }
  }

  useEffect(() => {
    load(true)
    const interval = setInterval(() => load(false), 30000)
    return () => clearInterval(interval)
  }, [slug, bib])

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
        <p className="text-gray-500 text-sm">Loading race data...</p>
      </div>
    </div>
  )

  if (error || !data) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="text-center max-w-sm">
        <p className="text-gray-700 font-semibold text-lg mb-2">Oops</p>
        <p className="text-gray-500 text-sm">{error ?? 'Unknown error'}</p>
      </div>
    </div>
  )

  const { team, raceState, runners, legs, logs, locations } = data

  const currentLegNumber = raceState?.currentLegNumber ?? 1
  const currentRunnerId = raceState?.currentRunnerId ?? null
  const currentRunner = runners.find(r => r.id === currentRunnerId) ?? null
  const completedLegs = logs.filter(l => l.endTime).length
  const totalLegs = legs.length || 36

  // Build location map keyed by runnerId for MapView
  const locationMap: Record<number, LocationPoint> = {}
  for (const l of locations) {
    locationMap[l.runnerId] = {
      latitude: l.latitude,
      longitude: l.longitude,
      accuracy: l.accuracy ?? null,
      timestamp: l.timestamp,
    }
  }

  // MapView expects Runner shape with expectedPace, averagePace etc. — add safe defaults
  const runnersForMap = runners.map(r => ({
    ...r,
    expectedPace: null,
    averagePace: null,
    phoneNumber: null,
    vanExchangeAlert: false,
    smsOptedIn: false,
    createdAt: '',
    updatedAt: '',
  }))

  // MapView expects LegAssignment shape
  const legsForMap = legs.map(l => ({
    ...l,
    raceId: team.raceId,
    expectedPace: l.estimatedPacePerMile ?? null,
  }))

  const displayedLegNumber = raceState?.raceStatus === 'completed' ? currentLegNumber + 1 : currentLegNumber

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-base font-bold text-gray-900 leading-tight">{team.name}</h1>
            <p className="text-xs text-gray-500 leading-tight">
              {team.raceName}
              {raceState?.raceStatus && (
                <span className={`ml-2 font-semibold ${
                  raceState.raceStatus === 'in_progress' ? 'text-green-600' :
                  raceState.raceStatus === 'completed' ? 'text-blue-600' : 'text-gray-400'
                }`}>
                  {raceState.raceStatus === 'in_progress' ? '● In Progress' :
                   raceState.raceStatus === 'completed' ? '✓ Finished' : 'Not Started'}
                </span>
              )}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs font-semibold text-blue-700">{completedLegs}/{totalLegs} legs</p>
            {lastUpdated && (
              <p className="text-xs text-gray-400">
                {lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1.5">
          <div
            className="bg-blue-600 h-1.5 rounded-full transition-all duration-500"
            style={{ width: `${(completedLegs / totalLegs) * 100}%` }}
          />
        </div>
      </div>

      {/* Map */}
      <div className="relative overflow-hidden" style={{ flex: '1 1 0', minHeight: 0 }}>
        {legsForMap.length > 0 && (
          <MapView
            runners={runnersForMap as any}
            legs={legsForMap as any}
            logs={logs as any}
            currentRunner={currentRunner as any}
            currentLegNumber={displayedLegNumber}
            locations={locationMap}
            flyToFullCourseTrigger={fullCourseTrigger}
          />
        )}

        {/* Active runner overlay */}
        {currentRunner && raceState?.raceStatus === 'in_progress' && (
          <div className="absolute top-3 left-3 z-[1000] bg-white rounded-lg shadow-lg px-3 py-2 border border-blue-200 pointer-events-none">
            <p className="text-xs text-blue-600 font-semibold leading-none mb-0.5">Now Running</p>
            <p className="text-sm font-bold text-gray-900 leading-none">{currentRunner.name}</p>
            <p className="text-xs text-gray-500">Leg {currentLegNumber} · Van {currentRunner.vanNumber}</p>
          </div>
        )}

        {/* Full course zoom */}
        <button
          onClick={() => setFullCourseTrigger(t => t + 1)}
          className="absolute bottom-16 left-3 z-[1000] bg-white border border-gray-300 rounded-lg px-3 py-2 text-xs font-semibold text-gray-700 shadow-md hover:bg-gray-50 flex items-center gap-1.5"
        >
          <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
          Full Course
        </button>
      </div>

      {/* Bottom panel */}
      <div className="flex-shrink-0 bg-white border-t border-gray-200" style={{ maxHeight: '35vh' }}>
        <div className="flex border-b border-gray-200 px-4">
          {(['runners', 'log'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`py-1.5 px-3 text-xs font-semibold border-b-2 transition-colors ${
                tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t === 'runners' ? 'Runners' : 'Leg Log'}
            </button>
          ))}
          {raceState?.raceStartTime && (
            <div className="ml-auto flex items-center text-xs text-gray-400 pr-1">
              Start: <span className="font-mono font-semibold text-gray-600 ml-1">{formatTimePT(new Date(raceState.raceStartTime))}</span>
            </div>
          )}
        </div>

        <div className="overflow-y-auto" style={{ maxHeight: 'calc(35vh - 32px)' }}>
          {tab === 'runners' && (
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-left font-semibold text-gray-500">#</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-gray-500">Name</th>
                  <th className="px-2 py-1.5 text-center font-semibold text-gray-500">Van</th>
                  <th className="px-2 py-1.5 text-center font-semibold text-gray-500">Legs</th>
                  <th className="px-2 py-1.5 text-center font-semibold text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {[...runners].sort((a, b) => a.runnerNumber - b.runnerNumber).map(runner => (
                  <tr key={runner.id} className={`border-t border-gray-100 ${runner.status === 'running' ? 'bg-blue-50' : ''}`}>
                    <td className="px-2 py-1.5 font-bold text-gray-600">{runner.runnerNumber}</td>
                    <td className="px-2 py-1.5 font-medium text-gray-900">{runner.name}</td>
                    <td className="px-2 py-1.5 text-center text-gray-500">{runner.vanNumber}</td>
                    <td className="px-2 py-1.5 text-center text-gray-500">
                      {logs.filter(l => l.runnerName === runner.name && l.endTime).length}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      {runner.status === 'running'
                        ? <span className="text-blue-600 font-semibold">▶ Running</span>
                        : <span className="text-gray-400">Resting</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab === 'log' && (
            logs.filter(l => l.endTime).length === 0 ? (
              <p className="text-center text-xs text-gray-400 py-6">No completed legs yet</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-center font-semibold text-gray-500">Leg</th>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-500">Runner</th>
                    <th className="px-2 py-1.5 text-center font-semibold text-gray-500">Time</th>
                    <th className="px-2 py-1.5 text-center font-semibold text-gray-500">Pace</th>
                  </tr>
                </thead>
                <tbody>
                  {[...logs].filter(l => l.endTime)
                    .sort((a, b) => (b.legNumber ?? 0) - (a.legNumber ?? 0))
                    .map(log => (
                      <tr key={log.id} className="border-t border-gray-100">
                        <td className="px-2 py-1.5 text-center font-bold text-gray-600">{log.legNumber}</td>
                        <td className="px-2 py-1.5 text-gray-700">{log.runnerName}</td>
                        <td className="px-2 py-1.5 text-center font-mono text-gray-600">
                          {log.actualDurationSeconds ? formatDuration(log.actualDurationSeconds) : '—'}
                        </td>
                        <td className="px-2 py-1.5 text-center font-mono text-gray-600">
                          {log.actualPacePerMile ? formatPace(log.actualPacePerMile) : '—'}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )
          )}
        </div>
      </div>

      <div className="flex-shrink-0 text-center text-xs text-gray-400 py-1 bg-white border-t border-gray-100">
        Relay Tracker · auto-refreshes every 30s
      </div>
    </div>
  )
}
